import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import {
  Card, Button, Badge, Select, Spinner, EmptyState, Modal, SearchInput,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '../../components/UI';
import { callAnalysisAPI, recordingsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineClipboardList, HiOutlineUpload, HiOutlineMicrophone,
  HiOutlineTrash, HiOutlineSparkles, HiOutlineRefresh,
} from 'react-icons/hi';
import { format } from 'date-fns';
import { LANGUAGE_OPTIONS, buildProviderOptions } from './languages';

// Analysis status badge colors
const STATUS_STYLES = {
  pending: 'bg-gray-100 text-gray-600',
  transcribing: 'bg-amber-100 text-amber-700',
  analyzing: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  pending: 'Pending', transcribing: 'Transcribing', analyzing: 'Analyzing',
  completed: 'Completed', failed: 'Failed',
};

// Filter chips: analysis state of each recording ('at-risk' shows only
// recordings where the AI flagged the deal as high/critical risk)
const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'none', label: 'Not Analyzed' },
  { value: 'pending', label: 'Pending' },
  { value: 'transcribing', label: 'Transcribing' },
  { value: 'analyzing', label: 'Analyzing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'at-risk', label: '⚠ At Risk' },
];

const buildPageList = (current, total) => {
  const pages = [];
  const set = new Set([1, total, current, current - 1, current + 1]);
  for (const p of set) {
    if (p >= 1 && p <= total) pages.push(p);
  }
  pages.sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < pages.length; i++) {
    result.push(pages[i]);
    if (i < pages.length - 1 && pages[i + 1] - pages[i] > 1) {
      result.push('...');
    }
  }
  return result;
};

// Format duration exactly the way the Recordings section does (m:ss, floor —
// never "1:60" from rounding the seconds up).
const formatDuration = (seconds) => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

// Score badge color by percentage (marking system)
const scoreColor = (overall) => {
  if (overall == null) return 'bg-gray-100 text-gray-500';
  if (overall >= 80) return 'bg-green-100 text-green-700';
  if (overall >= 60) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

// Analysis status badge — module-level so it isn't re-created on every render
const AnalysisBadge = ({ recording }) => {
  if (!recording.analysis) {
    return (
      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
        Not analyzed
      </span>
    );
  }
  const { status, score, dealRisk } = recording.analysis;
  // Completed analyses with marks show the percentage awarded to the recording
  if (status === 'completed' && score?.overall != null) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${scoreColor(score.overall)}`}>
          {score.overall}%{score.grade ? ` · ${score.grade}` : ''}
        </span>
        {/* At-risk deal flag — the AI heard warning signs in this call */}
        {['high', 'critical'].includes(dealRisk?.level) && (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
            dealRisk.level === 'critical' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'
          }`}>
            {dealRisk.level === 'critical' ? '‼' : '⚠'} At Risk
          </span>
        )}
      </span>
    );
  }
  // Completed without marks yet — still surface the risk flag if present
  if (status === 'completed' && ['high', 'critical'].includes(dealRisk?.level)) {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
        dealRisk.level === 'critical' ? 'bg-red-600 text-white' : 'bg-red-100 text-red-700'
      }`}>
        ⚠ At Risk
      </span>
    );
  }
  // Failed — show WHY (bug #18): the stored reason as a tooltip + a short line
  if (status === 'failed') {
    const reason = recording.analysis.error || 'Unknown error';
    return (
      <span className="inline-flex flex-col items-start gap-0.5 max-w-[240px]" title={reason}>
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES.failed}`}>
          {STATUS_LABELS.failed}
        </span>
        <span className="text-[11px] leading-tight text-red-600 line-clamp-2 break-words">{reason}</span>
      </span>
    );
  }
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
};

const getCallTypeBadge = (callType) => {
  switch (callType) {
    case 'incoming':
      return <Badge variant="info" size="sm">Incoming</Badge>;
    case 'missed':
      return <Badge variant="danger" size="sm">Missed</Badge>;
    default:
      return <Badge variant="primary" size="sm">Outgoing</Badge>;
  }
};

const CallAnalysis = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [language, setLanguage] = useState('multi');
  const [llmProvider, setLlmProvider] = useState('');
  const [ollamaModel, setOllamaModel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [retryingId, setRetryingId] = useState(null);

  // Provider availability (Deepgram key, LLM options)
  const { data: providersData } = useQuery({
    queryKey: ['call-analysis-providers'],
    queryFn: async () => {
      const response = await callAnalysisAPI.getProviders();
      return response.data?.data || {};
    },
    staleTime: 0, // always re-check so the config banner reflects the server immediately
    refetchOnMount: 'always',
  });
  const deepgramEnabled = providersData?.deepgramEnabled ?? true;
  const llmOptions = providersData?.llm?.available || {};
  const defaultProvider = providersData?.llm?.default || 'openai';
  const ollamaInfo = providersData?.llm?.ollama || {};
  const ollamaModels = ollamaInfo.models || [];
  // The provider that will actually be used for the next upload
  const effectiveProvider = llmProvider || defaultProvider;

  // Every recording from the Recordings module, joined with its analysis status
  const { data: listData, isLoading, refetch } = useQuery({
    queryKey: ['call-analysis-recordings', page, statusFilter, search],
    queryFn: async () => {
      const params = { page, limit: 20 };
      if (statusFilter) params.analysisStatus = statusFilter;
      if (search) params.search = search;
      try {
        const response = await callAnalysisAPI.listRecordings(params);
        return response.data?.data || {};
      } catch (error) {
        // Fallback: joined endpoint not available yet (backend not restarted) —
        // build the same list from the existing endpoints, merged client-side
        if (error.response?.status !== 404) throw error;
        const [recRes, anaRes] = await Promise.all([
          recordingsAPI.getAll({ page, limit: 20, ...(search ? { search } : {}) }),
          callAnalysisAPI.list({ page: 1, limit: 100 }),
        ]);
        const recs = recRes.data?.data || [];
        const analyses = anaRes.data?.data?.analyses || anaRes.data?.analyses || [];
        const byRecording = new Map(
          analyses.map((a) => [String(a.recordingId?._id || a.recordingId), a])
        );
        let merged = recs.map((rec) => ({
          ...rec,
          analysis: byRecording.get(String(rec._id)) || null,
        }));
        if (statusFilter === 'none') {
          merged = merged.filter((rec) => !rec.analysis);
        } else if (statusFilter) {
          merged = merged.filter((rec) => rec.analysis?.status === statusFilter);
        }
        return { recordings: merged, pagination: recRes.data?.pagination || {} };
      }
    },
    // Live status updates: poll while any recording on the page is still
    // processing (pending → transcribing → analyzing → completed/failed)
    refetchInterval: (query) => {
      const recs = query?.state?.data?.recordings || [];
      return recs.some((r) => ['pending', 'transcribing', 'analyzing'].includes(r.analysis?.status))
        ? 4000
        : false;
    },
  });

  // Stable reference (useMemo) so effect dependency arrays don't churn on
  // every render — the list only changes when the query data changes.
  const recordings = React.useMemo(() => listData?.recordings || [], [listData]);
  const pagination = listData?.pagination || {};

  // Auto-backfill: once per session, ask the server to queue analyses for ALL
  // recordings that don't have one yet — covers old recordings uploaded
  // before the auto-analysis feature existed. The server runs them in its
  // sequential background queue; statuses appear here via the live polling.
  const backfillTriedRef = React.useRef(false);
  React.useEffect(() => {
    if (backfillTriedRef.current || isLoading) return;
    if (!deepgramEnabled || recordings.length === 0) return;
    backfillTriedRef.current = true;
    callAnalysisAPI.backfill()
      .then((response) => {
        const queued = response.data?.data?.queued || 0;
        if (queued > 0) {
          toast.success(`Auto-analyzing ${queued} remaining recording(s) in the background`);
          refetch();
        }
      })
      .catch(() => {
        // Not an admin, manual mode, or nothing to do — silent. The manual
        // per-recording "Call Analysis" buttons keep working either way.
      });
  }, [recordings, isLoading, deepgramEnabled, refetch]);

  const fileInputRef = React.useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select an audio file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('recording', selectedFile);
      formData.append('language', language);
      if (llmProvider) formData.append('llmProvider', llmProvider);
      // Selected local Ollama model (only relevant for the Ollama provider)
      if ((llmProvider || defaultProvider) === 'ollama' && ollamaModel) {
        formData.append('llmModel', ollamaModel);
      }
      const response = await callAnalysisAPI.upload(formData);
      toast.success('Recording uploaded — analysis started');
      setShowUploadModal(false);
      setSelectedFile(null);
      refetch();
      navigate(`/app/call-analysis/${response.data?.data?.analysisId || response.data?.analysisId}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to upload recording');
    } finally {
      setUploading(false);
    }
  };

  // Per-recording "Call Analysis" button: opens the existing analysis, or
  // starts a new one for recordings that haven't been analyzed yet, then
  // opens the analysis detail page (which live-tracks processing).
  const handleAnalyze = async (rec) => {
    if (rec.analysis?._id) {
      navigate(`/app/call-analysis/${rec.analysis._id}`);
      return;
    }
    setAnalyzing(rec._id);
    try {
      const response = await callAnalysisAPI.analyzeExisting({ recordingId: rec._id });
      toast.success('Call analysis started');
      refetch();
      navigate(`/app/call-analysis/${response.data?.data?.analysisId || response.data?.analysisId}`);
    } catch (error) {
      // 409 = an analysis already exists for this recording → open it
      const existingId = error.response?.status === 409
        ? error.response?.data?.data?.analysisId
        : null;
      if (existingId) {
        navigate(`/app/call-analysis/${existingId}`);
      } else {
        toast.error(error.response?.data?.message || 'Failed to start call analysis');
      }
    } finally {
      setAnalyzing(null);
    }
  };

  // Retry a FAILED analysis straight from the list (bug #18)
  const handleRetry = async (rec) => {
    if (!rec.analysis?._id) return;
    setRetryingId(rec._id);
    try {
      await callAnalysisAPI.retry(rec.analysis._id);
      toast.success('Retrying analysis');
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to retry analysis');
    } finally {
      setRetryingId(null);
    }
  };

  const analyzeDisabledReason = (rec) => {
    if (rec.uploadStatus !== 'uploaded') return 'Recording file not uploaded yet';
    if (!deepgramEnabled) return 'Transcription is not configured on the server';
    return 'Run AI analysis on this recording';
  };

  const handleDelete = async (id) => {
    // The delete API removes a CallAnalysis document — it must be called
    // with the analysis id (rec.analysis._id), NOT the recording id.
    if (!id) {
      toast.error('No analysis found for this call');
      return;
    }
    try {
      await callAnalysisAPI.remove(id);
      toast.success('Analysis deleted — recording kept');
      setDeleting(null);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete analysis');
    }
  };

  // Permanently remove the recording itself (audio file + DB entry) —
  // works for uploads made here AND recordings coming from the Recordings
  // section. The backend also soft-deletes any linked analysis.
  const handleDeleteRecording = async () => {
    if (!deleting?._id) {
      toast.error('Recording not found');
      return;
    }
    try {
      await recordingsAPI.delete(deleting._id);
      toast.success('Recording deleted');
      setDeleting(null);
      refetch();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete recording');
    }
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <HiOutlineSparkles className="w-7 h-7 text-indigo-600" />
              Call Analysis
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              All call recordings from the Recordings section — click "Call Analysis" on any recording to get the AI transcript and coaching insights
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Icon + name forced onto one line (whitespace-nowrap);
                inline-block overrides Tailwind's svg{display:block} so the
                icon stays on the SAME line as the name. */}
            <Button onClick={() => setShowUploadModal(true)} className="flex items-center gap-2 whitespace-nowrap">
              <HiOutlineUpload className="w-5 h-5 inline-block flex-shrink-0" />
              Upload Recording
            </Button>
          </div>
        </div>

        {/* Deepgram not configured banner */}
        {!deepgramEnabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            Transcription is not configured. Ask your administrator to add{' '}
            <code className="bg-amber-100 px-1 rounded">DEEPGRAM_API_KEY</code> to the server environment.
          </div>
        )}

        {/* Search */}
        <Card padding="p-3 sm:p-4">
          <SearchInput
            value={search}
            onChange={(val) => { setSearch(val); setPage(1); }}
            placeholder="Search by phone or lead name..."
          />
        </Card>

        {/* Analysis status filter chips */}
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value || 'all'}
              onClick={() => { setStatusFilter(f.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${statusFilter === f.value ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* List */}
        <Card className="overflow-hidden">
          {isLoading ? (
            <div className="py-16 flex justify-center"><Spinner size="lg" /></div>
          ) : recordings.length === 0 ? (
            <EmptyState
              icon={<HiOutlineMicrophone className="w-8 h-8 text-gray-400" />}
              title="No recordings found"
              description="Call recordings from the Recordings section will appear here."
            />
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHead>
                    <tr>
                      <th className="text-left">Phone</th>
                      <th className="text-left">User</th>
                      <th className="text-left">Type</th>
                      <th className="text-left">Duration</th>
                      <th className="text-left">Date</th>
                      <th className="text-left">Analysis</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </TableHead>
                  <TableBody>
                    {recordings.map((rec) => (
                      <TableRow
                        key={rec._id}
                        className={rec.analysis?._id ? 'cursor-pointer hover:bg-gray-50' : ''}
                        onClick={() => rec.analysis?._id && navigate(`/app/call-analysis/${rec.analysis._id}`)}
                      >
                        <TableCell>
                          <div className="font-medium text-gray-900 whitespace-nowrap">{rec.phoneNumber}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[180px]">{rec.leadId?.name || 'Unknown'}</div>
                        </TableCell>
                        <TableCell className="text-gray-600 whitespace-nowrap">
                          {rec.userId?.firstName ? `${rec.userId.firstName} ${rec.userId.lastName}` : '—'}
                        </TableCell>
                        <TableCell>{getCallTypeBadge(rec.callType)}</TableCell>
                        <TableCell className="text-gray-600 whitespace-nowrap">{formatDuration(rec.duration)}</TableCell>
                        <TableCell className="text-gray-600 whitespace-nowrap">{rec.createdAt ? format(new Date(rec.createdAt), 'MMM d, yyyy h:mm a') : '—'}</TableCell>
                        <TableCell>
                          <AnalysisBadge recording={rec} />
                        </TableCell>
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end items-center gap-1">
                            <Button
                              size="sm"
                              onClick={() => handleAnalyze(rec)}
                              disabled={analyzing === rec._id || rec.uploadStatus !== 'uploaded' || !deepgramEnabled}
                              title={analyzeDisabledReason(rec)}
                              className="flex items-center gap-1.5 whitespace-nowrap"
                            >
                              {analyzing === rec._id
                                ? <span className="inline-flex flex-shrink-0"><Spinner size="sm" /></span>
                                : <HiOutlineSparkles className="w-4 h-4 inline-block flex-shrink-0" />}
                              Call Analysis
                            </Button>
                            {rec.analysis?.status === 'failed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRetry(rec)}
                                loading={retryingId === rec._id}
                                disabled={retryingId === rec._id || !deepgramEnabled}
                                title="Run the analysis again"
                                className="flex items-center gap-1 whitespace-nowrap"
                              >
                                <HiOutlineRefresh className="w-4 h-4 inline-block flex-shrink-0" />
                                Retry
                              </Button>
                            )}
                            {/* Delete — available for EVERY recording (uploads
                                AND entries coming from the Recordings section).
                                The modal offers analysis-only vs full removal. */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleting(rec)}
                              title={rec.analysis ? 'Delete analysis or recording' : 'Delete recording'}
                            >
                              <HiOutlineTrash className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y">
                {recordings.map((rec) => (
                  <div key={rec._id} className="p-4 space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span className="font-medium text-gray-900 text-sm truncate block">{rec.phoneNumber}</span>
                        <span className="text-xs text-gray-500 truncate block">{rec.leadId?.name || 'Unknown Lead'}</span>
                      </div>
                      <AnalysisBadge recording={rec} />
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{rec.userId?.firstName ? `${rec.userId.firstName} ${rec.userId.lastName}` : '—'}</span>
                      <span>{getCallTypeBadge(rec.callType)}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{formatDuration(rec.duration)}</span>
                      <span>{rec.createdAt ? format(new Date(rec.createdAt), 'MMM d, h:mm a') : '—'}</span>
                    </div>
                    <div className="flex items-center gap-1 pt-1">
                      <Button
                        size="sm"
                        className="flex-1 flex items-center justify-center gap-1.5 whitespace-nowrap"
                        onClick={() => handleAnalyze(rec)}
                        disabled={analyzing === rec._id || rec.uploadStatus !== 'uploaded' || !deepgramEnabled}
                      >
                        {analyzing === rec._id
                          ? <span className="inline-flex flex-shrink-0"><Spinner size="sm" /></span>
                          : <HiOutlineSparkles className="w-4 h-4 inline-block flex-shrink-0" />}
                        Call Analysis
                      </Button>
                      {rec.analysis?.status === 'failed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleRetry(rec)}
                          loading={retryingId === rec._id}
                          disabled={retryingId === rec._id || !deepgramEnabled}
                          className="flex items-center gap-1 whitespace-nowrap"
                        >
                          <HiOutlineRefresh className="w-4 h-4 inline-block flex-shrink-0" />
                          Retry
                        </Button>
                      )}
                      {/* Delete — for every recording (see desktop table note) */}
                      <Button size="sm" variant="ghost" onClick={() => setDeleting(rec)}>
                        <HiOutlineTrash className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-4 py-3 border-t flex items-center justify-between">
                  <span className="text-sm text-gray-500">
                    {pagination.total} total
                  </span>
                  <div className="flex gap-1">
                    {buildPageList(page, pagination.totalPages).map((p, i) =>
                      typeof p === 'number' ? (
                        <button key={i} onClick={() => setPage(p)}
                          className={`px-3 py-1 rounded text-sm ${p === page ? 'bg-indigo-600 text-white' : 'bg-gray-100 hover:bg-gray-200'}`}>
                          {p}
                        </button>
                      ) : (
                        <span key={i} className="px-1 py-1 text-gray-400">…</span>
                      )
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </Card>

        {/* Upload modal */}
        <Modal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} title="Upload Call Recording">
          <div className="space-y-4">
            {!deepgramEnabled && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                Transcription is not configured on the server. Ask your administrator to add DEEPGRAM_API_KEY.
              </div>
            )}
            {/* No analysis AI usable: no Claude key and Ollama not running. */}
            {deepgramEnabled && ollamaInfo.running === false && llmOptions.openai === false && llmOptions.anthropic === false && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <p className="font-medium mb-1">No analysis AI is available</p>
                <p>
                  Ask the platform administrator to add a Claude API key (SuperAdmin → Settings →
                  AI Call Analysis), or to start Ollama on the server.
                </p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,video/*"
              className="hidden"
              onChange={handleFileSelect}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition"
            >
              <HiOutlineMicrophone className="w-8 h-8 mx-auto text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">
                {selectedFile ? selectedFile.name : 'Click to select an audio file (mp3, wav, m4a, ogg…)'}
              </span>
            </button>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  options={LANGUAGE_OPTIONS}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">AI Provider</label>
                <Select
                  value={llmProvider || defaultProvider}
                  onChange={(e) => setLlmProvider(e.target.value)}
                  options={buildProviderOptions(llmOptions, ollamaInfo)}
                />
              </div>
            </div>

            {/* Ollama model picker — shown when Ollama is the active provider */}
            {effectiveProvider === 'ollama' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ollama Model</label>
                {ollamaModels.length > 0 ? (
                  <Select
                    value={ollamaModel}
                    onChange={(e) => setOllamaModel(e.target.value)}
                    options={[
                      { value: '', label: 'Auto — best installed model' },
                      ...ollamaModels.map((m) => ({ value: m, label: m })),
                    ]}
                  />
                ) : (
                  <div className={`rounded-lg p-3 text-sm ${ollamaInfo.running === false ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'}`}>
                    {ollamaInfo.running === false
                      ? 'Ollama is not running on the server machine. Start it (e.g. run "ollama serve"), or pick Claude.'
                      : 'No models are installed. Run "ollama pull llama3.1" on the server machine, or pick Claude.'}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  If the chosen model is missing, the server automatically uses the best installed one.
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setShowUploadModal(false)}>Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading || !selectedFile}>
                {uploading
                  ? <Spinner size="sm" />
                  : <><HiOutlineClipboardList className="w-4 h-4 mr-1 inline-block" /> Analyze Call</>}
              </Button>
            </div>
          </div>
        </Modal>

        {/* Delete confirm — offers analysis-only or full recording removal */}
        <Modal isOpen={!!deleting} onClose={() => setDeleting(null)}>
          <div className="p-2">
            <h3 className="text-lg font-semibold mb-1">
              {deleting?.analysis ? 'Delete analysis or recording?' : 'Delete recording?'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {deleting?.phoneNumber && (
                <span className="font-medium text-gray-700">
                  {deleting.phoneNumber}
                  {deleting.leadId?.name ? ` — ${deleting.leadId.name}` : ''}
                </span>
              )}
            </p>

            {deleting?.analysis && (
              <button
                type="button"
                onClick={() => handleDelete(deleting.analysis._id)}
                className="w-full text-left border border-gray-200 rounded-lg p-3 mb-2 hover:bg-gray-50 transition"
              >
                <div className="text-sm font-medium text-gray-900">Delete analysis only</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Removes the transcript and AI insights — the recording file is kept.
                </div>
              </button>
            )}

            <button
              type="button"
              onClick={handleDeleteRecording}
              className="w-full text-left border border-red-200 bg-red-50 rounded-lg p-3 mb-4 hover:bg-red-100 transition"
            >
              <div className="text-sm font-medium text-red-700">Delete recording</div>
              <div className="text-xs text-red-500 mt-0.5">
                Permanently removes the audio file{deleting?.analysis ? ', its analysis' : ''} and the recording entry. This cannot be undone.
              </div>
            </button>

            <div className="flex justify-end">
              <Button variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
            </div>
          </div>
        </Modal>
      </div>
    </Layout>
  );
};

export default CallAnalysis;